import { z } from "zod";

import { SimpleResponseSchema } from "@/schemas";
import type { AssignmentListResponse } from "@/schemas/assignment";
import { AssignmentListSchemaResponse } from "@/schemas/assignment";
import type { OfferListResponse } from "@/schemas/offer";
import { OfferListResponseSchema } from "@/schemas/offer";

import { apiRequest } from "./apiUtils";

import { SeriesDaySchema, ShiftDetailSchema } from "./employerDashboardApi";
import type { SeriesDay, ShiftDetail } from "./employerDashboardApi";

/**
 * Worker-facing shift detail (worker/shift/{shiftId}). Authorized by the worker's own
 * assignment/offer on the backend — unlike the employer business/shift/{shiftId} endpoint,
 * which is scoped to business ownership and 404s ("Shift not found") for a worker.
 * Reuses ShiftDetailSchema; the worker payload simply omits employer-only fields.
 */
export async function getWorkerShiftDetail(
  shiftId: number,
): Promise<ShiftDetail> {
  const data = await apiRequest<unknown>(`worker/shift/${shiftId}`, {
    method: "GET",
  });
  return ShiftDetailSchema.parse(data);
}

/** Every day of a multi-day series, with the caller's own status on each day. */
export async function getSeriesDays(seriesId: string): Promise<Array<SeriesDay>> {
  const data = await apiRequest<unknown>(`worker/shift/series/${encodeURIComponent(seriesId)}`, {
    method: "GET",
  });
  return z.array(SeriesDaySchema).parse(data);
}

const ApplyDaysResponseSchema = z.object({
  success: z.boolean().default(true),
  applied: z.array(z.number()).default([]),
  failed: z
    .array(z.object({ shift_id: z.number(), reason: z.string().nullable().optional() }))
    .default([]),
});
export type ApplyDaysResponse = z.infer<typeof ApplyDaysResponseSchema>;

/** Apply to several days (single-day shifts) at once; each day succeeds or fails on its own. */
export async function applyToDays(shiftIds: Array<number>): Promise<ApplyDaysResponse> {
  const data = await apiRequest<unknown>("worker/shift/apply-days", {
    method: "POST",
    body: JSON.stringify({ shift_ids: shiftIds }),
  });
  return ApplyDaysResponseSchema.parse(data);
}

export async function listWorkerShifts({
  pageSize = 100,
  startDate,
  endDate,
}: {
  pageSize?: number;
  startDate?: string;
  endDate?: string;
} = {}): Promise<AssignmentListResponse> {
  const request: any = {
    page: 0,
    page_size: pageSize,
    sort_order: 'desc',
  };
  if (startDate) {
    request.start_date = startDate;
  }
  if (endDate) {
    request.end_date = endDate;
  }
  const data = await apiRequest<unknown>("worker/shift/list-assignment", {
    method: "POST",
    body: JSON.stringify(request),
  });
  const parsed = AssignmentListSchemaResponse.parse(data);
  parsed.assignments = parsed.assignments.sort(
    (a, b) =>
      new Date(a.start_date_time).getTime() -
      new Date(b.start_date_time).getTime(),
  );
  return parsed;
}

export async function listWorkerOffers(): Promise<OfferListResponse> {
  const data = await apiRequest<unknown>("worker/shift/list-offer", {
    method: "POST",
    body: JSON.stringify({
      page: 0,
      page_size: 10,
    }),
  });
  return OfferListResponseSchema.parse(data);
}

export async function updateWorkerOffer(
  offerId: string | number,
  interest_status: "PENDING" | "ACCEPTED" | "REJECTED",
): Promise<{ success: boolean; message?: string }> {
  const data = await apiRequest<unknown>(`worker/shift/offer/${offerId}`, {
    method: "PATCH",
    body: JSON.stringify({ interest_status }),
  });
  const parsed = SimpleResponseSchema.parse(data);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function addOrUpdateWorkerOffer({
  shiftId,
  interestStatus,
}: {
  shiftId: string | number;
  interestStatus: string;
}): Promise<{ success: boolean; message?: string }> {
  const data = await apiRequest<unknown>("worker/shift/add-update-offer", {
    method: "POST",
    body: JSON.stringify({
      shift_id: shiftId,
      interest_status: interestStatus,
    }),
  });
  const parsed = SimpleResponseSchema.parse(data);
  return { success: parsed.success, message: parsed.message ?? undefined };
}
